import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { MessagingProvider } from "./contexts/MessagingContext";
import { VoucherProvider } from "./contexts/VoucherContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Layout from "./components/layout/Layout";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { VerifiedGuard } from "./components/routing/AuthGuard";
import DeepLinkHandler from "./components/common/DeepLinkHandler";
import { CardSkeleton, BaakasLogoLoader } from "./components/common/LoadingSkeleton";
import BaakasErrorDialog from "./components/common/BaakasErrorDialog";
import RequireAuth from "./components/routing/RequireAuth";
import { setErrorSuppression } from "./lib/errors/notify";
import { secureLog } from "./lib/errors/logger";
// AFFILIATE (disabled for now): re-enable extractAndStoreFromUrl + AffiliateLandingPage route below
// import { extractAndStoreFromUrl } from "./services/affiliateTracking";
// Using the new enhanced CategoriesPage with better sidebar and hierarchical category selection
import CategoriesPage from "./pages/CategoriesPageNew";

// PERFORMANCE: Lazy load all pages for code-splitting (reduces initial bundle by 40-60%)
// Critical pages loaded immediately (HomePage)
const HomePage = lazy(() => import("./pages/HomePage"));

// Auth pages - lazy loaded
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(
  () => import("./pages/auth/ForgotPasswordPage")
);
const EmailVerificationPage = lazy(
  () => import("./pages/auth/EmailVerificationPage")
);
const AuthActionHandler = lazy(() => import("./pages/auth/AuthActionHandler"));

// Shop pages - lazy loaded
const ProductDetailPage = lazy(() => import("./pages/shop/ProductDetailPage"));
const CartPage = lazy(() => import("./pages/shop/CartPage"));
const WishlistPage = lazy(() => import("./pages/shop/WishlistPage"));
const RecentlyViewedPage = lazy(
  () => import("./pages/shop/RecentlyViewedPage")
);
const PopularProductsPage = lazy(
  () => import("./pages/shop/PopularProductsPage")
);
const FeaturedProductsPage = lazy(
  () => import("./pages/shop/FeaturedProductsPage")
);
const FlashSalePage = lazy(() => import("./pages/shop/FlashSalePage"));
// const AffiliateLandingPage = lazy(() => import("./pages/AffiliateLandingPage"));

// Checkout pages - lazy loaded
const CheckoutPage = lazy(() => import("./pages/checkout/CheckoutPage"));
const OrderSuccessPage = lazy(
  () => import("./pages/checkout/OrderSuccessPage")
);
const PaymentResultPage = lazy(
  () => import("./pages/checkout/PaymentResultPage")
);

// User pages - lazy loaded
const EditProfilePage = lazy(() => import("./pages/user/EditProfilePage"));
const ProfilePage = lazy(() => import("./pages/user/ProfilePage"));
const AddressesPage = lazy(() => import("./pages/account/AddressesPage"));
const OrderTrackingPage = lazy(() => import("./pages/user/OrderTrackingPage"));
const OrderHistoryPage = lazy(() => import("./pages/user/OrderHistoryPage"));
const VouchersPage = lazy(() => import("./pages/user/VouchersPage"));
const FollowingPage = lazy(() => import("./pages/user/FollowingPage"));
const LoyaltyPointsPage = lazy(() => import("./pages/user/LoyaltyPointsPage"));
const ReturnRequestsPage = lazy(
  () => import("./pages/user/ReturnRequestsPage")
);

// Order pages - lazy loaded
const OrdersListPage = lazy(() => import("./pages/orders/index"));
const OrderDetailsPage = lazy(() => import("./pages/orders/[id]"));
const InvoicePage = lazy(() => import("./pages/invoice/[orderId]"));

// Store pages - lazy loaded
const StoresDirectoryPage = lazy(
  () => import("./pages/stores/StoresDirectoryPage")
);
const StorePage = lazy(() => import("./pages/stores/StorePage"));
const AllSellersPage = lazy(() => import("./pages/stores/AllSellersPage"));

// Info pages - lazy loaded
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ShippingInfoPage = lazy(() => import("./pages/ShippingInfoPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const BaakasUniversityPage = lazy(() => import("./pages/BaakasUniversityPage"));
const LoyaltyCoinsPage = lazy(() => import("./pages/LoyaltyCoinsPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const CookiePolicyPage = lazy(() => import("./pages/legal/CookiePolicy"));

// Payment pages - lazy loaded
const PaymentSuccessPage = lazy(
  () => import("./pages/payment/PaymentSuccessPage")
);
const KhaltiPaymentPage = lazy(
  () => import("./pages/payment/KhaltiPaymentPage")
);
const KhaltiCancelPage = lazy(() => import("./pages/payment/KhaltiCancelPage"));

// Other pages - lazy loaded
const MessagesPage = lazy(() => import("./pages/messaging/MessagesPage"));
const SettingsLayout = lazy(() => import("./pages/settings/SettingsLayout"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Settings sections - lazy loaded
const Profile = lazy(() => import("./pages/settings/sections/Profile"));
const Account = lazy(() => import("./pages/settings/sections/Account"));
const Addresses = lazy(() => import("./pages/settings/sections/Addresses"));
const PrivacySecurity = lazy(
  () => import("./pages/settings/sections/PrivacySecurity")
);
const PaymentsWallet = lazy(
  () => import("./pages/settings/sections/PaymentsWallet")
);
const OrdersReviews = lazy(
  () => import("./pages/settings/sections/OrdersReviews")
);
const LoyaltyCoins = lazy(
  () => import("./pages/settings/sections/LoyaltyCoins")
);
const HelpFAQ = lazy(() => import("./pages/settings/sections/HelpFAQ"));
const AboutApp = lazy(() => import("./pages/settings/sections/AboutApp"));
const DangerZone = lazy(() => import("./pages/settings/sections/DangerZone"));

// Wrapper component to apply VerifiedGuard to all routes except auth pages and public pages
const VerifiedGuardWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const authPages = [
    "/login",
    "/register",
    "/forgot-password",
    "/email-verification",
    "/auth/login",
  ];
  const publicPages = [
    "/",
    "/categories",
    "/product",
    "/contact",
    "/about",
    "/help",
    "/support",
    "/terms",
    "/privacy",
    "/legal/cookies",
    "/shipping",
    "/returns",
    "/baakas-university",
    "/feedback",
    "/stores",
    "/store-page",
    "/popular-products",
    "/featured-products",
    "/flash-sale",
    "/search",
    // "/affiliate",
  ];

  const isAuthPage = authPages.includes(location.pathname);
  const isPublicPage = publicPages.some(
    (page) =>
      location.pathname === page || location.pathname.startsWith(page + "/")
  );

  if (isAuthPage || isPublicPage) {
    return <>{children}</>;
  }

  return <VerifiedGuard>{children}</VerifiedGuard>;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const e = error as any;
        if (e?.code === "TRANSIENT_BACKEND_UNAVAILABLE")
          return failureCount < 3; // 3 attempts
        if (e?.code === "DB_INDEX_REQUIRED") return false; // Don't retry index errors
        if (e?.code === "DB_UNAVAILABLE") return failureCount < 2; // 2 attempts for DB issues
        if (e?.code === "FUNC_UNAVAILABLE") return failureCount < 1; // 1 attempt for function issues
        return failureCount < 1; // others: 1 safe retry max
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000), // 1s,2s,4s,8s cap
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      networkMode: "online",
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        const e = error as any;
        if (e?.code === "TRANSIENT_BACKEND_UNAVAILABLE")
          return failureCount < 2;
        if (e?.code === "DB_INDEX_REQUIRED") return false; // Don't retry index errors
        if (e?.code === "DB_UNAVAILABLE") return failureCount < 1; // 1 attempt for DB issues
        return false;
      },
      retryDelay: (attempt) => 1000 * 2 ** attempt,
    },
  },
});

// Component to redirect /products to /categories while preserving query parameters
const ProductsRedirect: React.FC = () => {
  const location = useLocation();
  const search = location.search || "";
  return <Navigate to={`/categories${search}`} replace />;
};

function App() {
  // NOTE: Canonical-domain redirects (www <-> apex) must be handled at the
  // hosting layer (Vercel project "Domains" settings), NOT in client code.
  // A previous React-side redirect here fought Vercel's own redirect and
  // caused an infinite reload loop when the host's primary domain was set
  // to www.baakas.com.np. Configure the canonical domain in Vercel once
  // and let it 308-redirect at the edge.

  // AFFILIATE (disabled for now): capture attribution from URL on app load (e.g. ?aff_id= or ?affiliate=)
  // React.useEffect(() => {
  //   extractAndStoreFromUrl(window.location.href).catch(() => {});
  // }, []);

  // Temporarily suppress errors during app initialization
  React.useEffect(() => {
    // Enable error suppression for the first 15 seconds to prevent initial load errors
    // This gives Firebase operations time to complete and indexes to be built
    setErrorSuppression(true);

    const timer = setTimeout(() => {
      setErrorSuppression(false);
    }, 15000);

    return () => {
      clearTimeout(timer);
      setErrorSuppression(false);
    };
  }, []);

  // Suppress Firebase WebChannel console errors (ERR_BLOCKED_BY_CLIENT)
  // These are harmless errors caused by browser extensions blocking Firebase requests
  // Also suppress verbose Firebase SDK logs unless debug flag is enabled
  React.useEffect(() => {
    // Only suppress Firebase SDK logs in production or when debug logs are disabled
    const shouldSuppressFirebaseLogs = process.env.NODE_ENV === 'production' || 
                                       process.env.VITE_ENABLE_DEBUG_LOGS !== 'true';
    
    // Store original console methods
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    // Override console.error to filter Firebase WebChannel errors and Firestore internal assertion errors
    console.error = (...args: any[]) => {
      const message = args.join(" ").toLowerCase();
      const fullMessage = args.join(" ");
      
      // Suppress Firebase SDK verbose logs (unless debug flag is enabled)
      if (shouldSuppressFirebaseLogs && (
        fullMessage.includes("@firebase/firestore:") ||
        fullMessage.includes("@firebase/") ||
        (fullMessage.includes("Firestore") && fullMessage.includes("RestConnection")) ||
        (fullMessage.includes("Firestore") && fullMessage.includes("WebChannelConnection")) ||
        (fullMessage.includes("Firestore") && fullMessage.includes("RPC"))
      )) {
        // Silently ignore Firebase SDK internal logs
        return;
      }
      
      // Suppress Firebase WebChannel connection errors that are blocked by extensions
      // Also suppress Firestore internal assertion errors (harmless SDK-level errors)
      if (
        message.includes("err_blocked_by_client") ||
        (message.includes("firestore") &&
          message.includes("webchannel") &&
          message.includes("blocked")) ||
        (message.includes("firestore.googleapis.com") &&
          message.includes("terminate") &&
          message.includes("blocked")) ||
        (message.includes("firestore") &&
          message.includes("internal assertion failed")) ||
        (message.includes("firestore") &&
          message.includes("unexpected state"))
      ) {
        // Silently ignore - these are expected when extensions block Firebase
        // or when Firestore SDK encounters internal state issues (harmless)
        return;
      }
      
      // Suppress source map errors (browser/devtools issue, not fixable in code)
      if (
        message.includes("source map error") ||
        message.includes("parsesourcemapinput") ||
        message.includes("sourcemap") ||
        fullMessage.includes("Source map error") ||
        fullMessage.includes("parseSourceMapInput")
      ) {
        // Silently ignore - these are browser/devtools issues with source maps
        return;
      }
      
      // Call original console.error for other errors
      originalError.apply(console, args);
    };

    // Override console.warn to filter Firebase WebChannel warnings and SDK logs
    console.warn = (...args: any[]) => {
      const message = args.join(" ").toLowerCase();
      const fullMessage = args.join(" ");
      
      // Suppress Firebase SDK verbose logs (unless debug flag is enabled)
      if (shouldSuppressFirebaseLogs && (
        fullMessage.includes("@firebase/firestore:") ||
        fullMessage.includes("@firebase/") ||
        (fullMessage.includes("Firestore") && fullMessage.includes("RestConnection")) ||
        (fullMessage.includes("Firestore") && fullMessage.includes("WebChannelConnection"))
      )) {
        // Silently ignore Firebase SDK internal logs
        return;
      }
      
      // Suppress Firebase WebChannel connection warnings
      if (
        message.includes("err_blocked_by_client") ||
        (message.includes("firestore") &&
          message.includes("webchannel") &&
          message.includes("blocked"))
      ) {
        // Silently ignore - these are expected when extensions block Firebase
        return;
      }
      
      // Suppress preload warnings (resource preloaded but not used)
      if (
        message.includes("preloaded with link preload was not used") ||
        message.includes("preload was not used within a few seconds")
      ) {
        // Silently ignore - these are harmless browser warnings about unused preloads
        return;
      }
      
      // Call original console.warn for other warnings
      originalWarn.apply(console, args);
    };

    // Override console.log to filter Firebase SDK logs
    console.log = (...args: any[]) => {
      const fullMessage = args.join(" ");
      
      // Suppress Firebase SDK verbose logs (unless debug flag is enabled)
      if (shouldSuppressFirebaseLogs && (
        fullMessage.includes("@firebase/firestore:") ||
        fullMessage.includes("@firebase/") ||
        (fullMessage.includes("Firestore") && fullMessage.includes("RestConnection")) ||
        (fullMessage.includes("Firestore") && fullMessage.includes("WebChannelConnection"))
      )) {
        // Silently ignore Firebase SDK internal logs
        return;
      }
      
      // Call original console.log for other logs
      originalLog.apply(console, args);
    };

    return () => {
      // Restore original console methods on cleanup
      console.error = originalError;
      console.warn = originalWarn;
      console.log = originalLog;
    };
  }, []);

  // Add global error handler to catch any unhandled errors
  React.useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      // Suppress common Firebase/Firestore errors including internal assertion errors
      const errorMessage = event.message?.toLowerCase() || "";
      if (
        errorMessage.includes("index") ||
        errorMessage.includes("permission") ||
        errorMessage.includes("network") ||
        errorMessage.includes("firestore") ||
        errorMessage.includes("firebase") ||
        errorMessage.includes("unavailable") ||
        errorMessage.includes("quic") ||
        errorMessage.includes("protocol") ||
        errorMessage.includes("blocked") ||
        errorMessage.includes("client") ||
        errorMessage.includes("internal assertion failed") ||
        errorMessage.includes("unexpected state")
      ) {
        secureLog("warn", "Global error suppressed:", event.message);
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", handleGlobalError);

    // Also handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorCode = error?.code || "";
      const errorMessage = String(
        error?.message || error?.toString() || ""
      ).toLowerCase();

      // Suppress expected errors (permission-denied, network issues, Firestore internal assertion errors, etc.)
      if (
        errorCode === "permission-denied" ||
        errorCode === "PERMISSION_DENIED" ||
        errorMessage.includes("index") ||
        errorMessage.includes("permission") ||
        errorMessage.includes("network") ||
        errorMessage.includes("firestore") ||
        errorMessage.includes("firebase") ||
        errorMessage.includes("unavailable") ||
        errorMessage.includes("quic") ||
        errorMessage.includes("protocol") ||
        errorMessage.includes("blocked") ||
        errorMessage.includes("client") ||
        errorMessage.includes("err_blocked_by_client") ||
        errorMessage.includes("internal assertion failed") ||
        errorMessage.includes("unexpected state")
      ) {
        // Silently suppress - these are expected errors
        event.preventDefault();
        return;
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleGlobalError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <VoucherProvider>
            <MessagingProvider>
              <CartProvider>
                <WishlistProvider>
                  <SettingsProvider>
                    <CookieConsentProvider>
                    <Router
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <DeepLinkHandler />
                    <div className="App">
                      <AppErrorBoundary>
                        <Layout>
                          <VerifiedGuardWrapper>
                            <Suspense
                              fallback={
                                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                                  <div className="text-center">
                                    <BaakasLogoLoader size="xl" className="mx-auto mb-4" aria-label="Loading page" />
                                    <p className="text-gray-600 font-medium">Loading...</p>
                                  </div>
                                </div>
                              }
                            >
                              <Routes>
                                <Route index element={<HomePage />} />
                                <Route path="login" element={<LoginPage />} />
                                <Route
                                  path="auth/login"
                                  element={<AuthActionHandler />}
                                />
                                <Route
                                  path="register"
                                  element={<RegisterPage />}
                                />
                                <Route
                                  path="forgot-password"
                                  element={<ForgotPasswordPage />}
                                />
                                <Route
                                  path="email-verification"
                                  element={<EmailVerificationPage />}
                                />
                                <Route
                                  path="products"
                                  element={<ProductsRedirect />}
                                />
                                <Route
                                  path="product/:id"
                                  element={<ProductDetailPage />}
                                />
                                <Route path="cart" element={<CartPage />} />
                                <Route
                                  path="checkout"
                                  element={<CheckoutPage />}
                                />
                                <Route
                                  path="order-success/:orderId"
                                  element={<OrderSuccessPage />}
                                />

                                <Route
                                  path="payment/success/:orderId"
                                  element={<PaymentSuccessPage />}
                                />
                                <Route
                                  path="payment/khalti/:orderId"
                                  element={<KhaltiPaymentPage />}
                                />

                                <Route
                                  path="payment/khalti/cancel"
                                  element={<KhaltiCancelPage />}
                                />
                                <Route
                                  path="delete-account"
                                  element={<DeleteAccountPage />}
                                />

                                {/* Dev routes - only accessible in development */}

                                <Route
                                  path="wishlist"
                                  element={<WishlistPage />}
                                />
                                <Route
                                  path="recently-viewed"
                                  element={<RecentlyViewedPage />}
                                />
                                <Route
                                  path="categories"
                                  element={<CategoriesPage />}
                                />
                                {/* <Route path="category/*" element={<Navigate to="/categories" replace />} /> */}
                                <Route
                                  path="contact"
                                  element={<ContactPage />}
                                />
                                <Route path="about" element={<AboutPage />} />
                                <Route
                                  path="help"
                                  element={<HelpCenterPage />}
                                />
                                <Route
                                  path="support"
                                  element={<SupportPage />}
                                />
                                <Route path="terms" element={<TermsPage />} />
                                <Route
                                  path="privacy"
                                  element={<PrivacyPage />}
                                />
                                <Route
                                  path="legal/cookies"
                                  element={<CookiePolicyPage />}
                                />
                                <Route
                                  path="shipping"
                                  element={<ShippingInfoPage />}
                                />
                                <Route
                                  path="returns"
                                  element={<ReturnsPage />}
                                />
                                {/* <Route path="gift-card" element={<GiftCardPage />} /> */}
                                <Route
                                  path="baakas-university"
                                  element={<BaakasUniversityPage />}
                                />
                                <Route
                                  path="loyalty-coins"
                                  element={<LoyaltyCoinsPage />}
                                />
                                <Route
                                  path="profile"
                                  element={<ProfilePage />}
                                />
                                <Route
                                  path="edit-profile"
                                  element={<EditProfilePage />}
                                />
                                <Route
                                  path="profile/edit"
                                  element={<EditProfilePage />}
                                />
                                <Route
                                  path="addresses"
                                  element={<AddressesPage />}
                                />
                                <Route
                                  path="order-tracking"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <OrderTrackingPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="order-tracking/:orderId"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <OrderTrackingPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="order-history"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <OrderHistoryPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="return-requests"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <ReturnRequestsPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="orders"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <OrdersListPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="orders/:id"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <OrderDetailsPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="invoice/:orderId"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <InvoicePage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                {/* <Route path="wallet" element={<WalletPage />} /> */}
                                <Route
                                  path="vouchers"
                                  element={<VouchersPage />}
                                />
                                <Route
                                  path="following"
                                  element={<FollowingPage />}
                                />
                                <Route
                                  path="loyalty-points"
                                  element={<LoyaltyPointsPage />}
                                />
                                <Route
                                  path="messaging"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <MessagesPage />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="stores"
                                  element={<StoresDirectoryPage />}
                                />
                                <Route
                                  path="stores/all"
                                  element={<AllSellersPage />}
                                />
                                <Route
                                  path="store-page/:slugOrId"
                                  element={<StorePage />}
                                />
                                <Route
                                  path="popular-products"
                                  element={<PopularProductsPage />}
                                />
                                <Route
                                  path="featured-products"
                                  element={<FeaturedProductsPage />}
                                />
                                <Route
                                  path="flash-sale"
                                  element={<FlashSalePage />}
                                />
                                {/* AFFILIATE (disabled for now)
                                <Route
                                  path="affiliate"
                                  element={<AffiliateLandingPage />}
                                />
                                */}
                                <Route path="search" element={<SearchPage />} />
                                <Route path="feedback" element={<FeedbackPage />} />

                                <Route
                                  path="settings/*"
                                  element={
                                    <Suspense fallback={<CardSkeleton />}>
                                      <RequireAuth>
                                        <SettingsLayout />
                                      </RequireAuth>
                                    </Suspense>
                                  }
                                >
                                  <Route index element={<Profile />} />
                                  <Route path="profile" element={<Profile />} />
                                  <Route path="account" element={<Account />} />
                                  <Route
                                    path="addresses"
                                    element={<Addresses />}
                                  />
                                  <Route
                                    path="privacy"
                                    element={<PrivacySecurity />}
                                  />
                                  <Route
                                    path="payments"
                                    element={<PaymentsWallet />}
                                  />
                                  <Route
                                    path="orders"
                                    element={<OrdersReviews />}
                                  />
                                  <Route
                                    path="loyalty"
                                    element={<LoyaltyCoins />}
                                  />
                                  <Route path="help" element={<HelpFAQ />} />
                                  <Route path="about" element={<AboutApp />} />
                                  <Route
                                    path="danger"
                                    element={<DangerZone />}
                                  />
                                </Route>
                                <Route
                                  path="checkout/result"
                                  element={<PaymentResultPage />}
                                />
                                <Route path="*" element={<NotFoundPage />} />
                              </Routes>
                            </Suspense>
                          </VerifiedGuardWrapper>
                        </Layout>
                      </AppErrorBoundary>

                      {/* Scroll to Top Button */}
                      <ScrollToTop />

                      {/* Toast Notifications */}
                      <BaakasErrorDialog />
                      <Toaster
                        position="top-center"
                        containerClassName="!top-20 sm:!top-4"
                        gutter={8}
                        toastOptions={{
                          duration: 2500,
                          style: {
                            background: "#363636",
                            color: "#fff",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            fontSize: "14px",
                            maxWidth: "calc(100vw - 32px)",
                            width: "auto",
                            minWidth: "280px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          },
                          className: "toast-notification",
                          success: {
                            duration: 2000,
                            iconTheme: {
                              primary: "#4CAF50",
                              secondary: "#fff",
                            },
                          },
                          error: {
                            duration: 4000,
                            iconTheme: {
                              primary: "#D32F2F",
                              secondary: "#fff",
                            },
                          },
                        }}
                      />
                      <CookieConsentBanner />
                    </div>
                  </Router>
                  </CookieConsentProvider>
                </SettingsProvider>
              </WishlistProvider>
            </CartProvider>
          </MessagingProvider>
          </VoucherProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
